import { PixelUnit } from '@starrez/starryui/dist/Theme';
import { color, Icon } from '@starrez/starryui9';
import * as React from 'react';
import styled from 'styled-components';
import { IData } from '..';
import { strings } from '../../../../.localisation';

export interface IDisplayFilterButtonProps {
    active: boolean;
    caption: string;
    dropdownVisible: boolean;
    onPickFilter?: () => void;
    onRemove?: () => void;
    value: IData[];
    removable?: boolean;
}

export const BuildValueCaption = (value: IData[]) => {
    return value.map((item) => item.caption).join(', ');
};

export const DisplayFilterButton = React.forwardRef(
    (
        { active, caption, value, onRemove, onPickFilter, dropdownVisible, removable = true }: IDisplayFilterButtonProps,
        ref: React.Ref<HTMLButtonElement>
    ) => {
        const handlePickFilter = (e: React.MouseEvent<HTMLButtonElement>) => {
            e.nativeEvent.stopImmediatePropagation();
            onPickFilter && onPickFilter();
        };

        const captionRef = React.createRef<HTMLSpanElement>();
        const [showMore, setShowMore] = React.useState(false);

        const buildValue = () => {
            if (value && value.length > 0) {
                return (
                    <>
                        {caption}
                        <span>{strings.FilterBar_Label_Is}</span>
                        {BuildValueCaption(value)}
                    </>
                );
            } else {
                if (!dropdownVisible) {
                    return strings.formatString(strings.FilterBar_Label_IsMissing, caption).toString();
                }
            }

            return `${caption}`;
        };

        const calculateStyle = () => {
            if (!dropdownVisible && (!value || value.length === 0)) {
                return 'Error';
            }

            if (active) {
                return 'Active';
            }

            return '';
        };

        React.useEffect(() => {
            if (
                value &&
                captionRef?.current &&
                captionRef?.current &&
                value?.length > 1 &&
                captionRef?.current?.scrollWidth > captionRef?.current?.clientWidth
            ) {
                setShowMore(true);
            } else {
                setShowMore(false);
            }
        }, [value]);

        return (
            <WrapperDiv removable={removable}>
                <Button
                    removable={removable}
                    aria-label={removable ? strings.FilterBar_Label_EditFilter : strings.FilterBar_Label_FixedFilter}
                    title={removable ? strings.FilterBar_Label_EditFilter : strings.FilterBar_Label_FixedFilter}
                    onClick={handlePickFilter}
                    ref={ref}
                    Style={calculateStyle()}
                    type="button"
                >
                    <CaptionSpan ref={captionRef}>{buildValue()}</CaptionSpan>
                    {showMore && <span style={{ paddingLeft: '4px' }}>{strings.FilterBar_Label_PlusMore}</span>}
                </Button>

                {removable && (
                    <Button
                        removable={removable}
                        aria-label={strings.FilterBar_Label_RemoveFilter}
                        onClick={onRemove}
                        Style={calculateStyle()}
                        title={strings.FilterBar_Label_RemoveFilter}
                        type="button"
                    >
                        <Icon icon="CloseThick" height="16px" width="16px" />
                    </Button>
                )}
            </WrapperDiv>
        );
    }
);

export const WrapperDiv = styled.div<{ removable: boolean }>`
    display: grid;
    grid-template-columns: auto ${(props) => (props.removable ? PixelUnit(4) : '')};
    align-items: center;

    height: ${PixelUnit(4)};

    > button:first-of-type {
        border-radius: 4px 0 0 4px;
    }

    > button:last-of-type {
        border-radius: ${(props) => (props.removable ? '0 4px 4px 0' : '4px')};
    }
`;

interface IButton {
    Style: 'Active' | 'Error' | '';
    removable: boolean;
}

export const buildButtonColors = (props: IButton) => {
    if (props.Style === 'Active') {
        return {
            background: color.Blue.B20,
            color: color.Blue.B60,
        };
    } else if (props.Style === 'Error') {
        return {
            background: color.Red.R20,
            color: color.Red.R80,
        };
    }

    return {
        background: color.Grey.G10,
        color: color.Grey.G80,
    };
};

export const Button = styled.button<IButton>`
    box-sizing: border-box;
    height: 100%;
    padding: 0 8px;
    margin: 0;
    border: 0;

    cursor: ${(props) => (props.removable ? 'pointer' : 'default')};
    user-select: none;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;

    ${buildButtonColors}

    font-size: 14px !important;

    &:hover {
        color: ${color.Blue.B60};
    }

    &:hover span {
        color: ${color.Blue.B60};
    }

    &:focus {
        position: relative;
    }
`;

export const CaptionSpan = styled.span`
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    > span {
        padding: 0 4px;
        color: ${color.Grey.G60};
    }
`;
